import React from "react"
import Plot from "react-plotly.js"
import {useTheme} from "@material-ui/core"

// Models
import {LeadEnum, Signal} from "../../../models"

// Theme
import {ModernTheme} from "../../../../../theme/modern/tokens"

// Geometry
import {
    computeEcgGeometry,
    computePulse,
    COLUMN_GAP_MM,
    EcgLayoutKey,
    LEAD_ORDER,
    TOP_BASELINE_MM,
} from "./geometry"
import {formatClock} from "./window"

type ModernEcgGridProps = {
    /** mm/s — 12.5, 25 or 50. */
    speed: number
    /** mm/mV — 5, 10 or 20. */
    amplitude: number
    ecgData: Signal[]
    layout: EcgLayoutKey
    windowStartSec: number
    longRecords: boolean
}

/**
 * Theme-aware fork of ../../ECGGraph/ECGGrid, extended with the prototype's
 * layout / speed / gain controls.
 *
 * The picture is Classic's: same paper scale (see ./geometry), same grid, same
 * #555555 trace and #0000FF lead labels, same 12 x 1 / 25 mm/s / 10 mm/mV
 * defaults. What the fork adds is a) the layout / speed / gain controls, which
 * Classic has none of, and b) reading-room (dark) mode — Classic hard-codes its
 * palette, which under a dark page renders as a white slab, so this file reads
 * every colour from `theme.modern` and the LIGHT tokens are set to Classic's
 * exact values: paper / gridMinor / gridMajor / trace / beat / ink.
 *
 * All geometry comes from ./geometry, which <ModernCaliperLayer/> also reads —
 * see the note there on why the two must not diverge.
 *
 * Classic keeps using the original file, untouched.
 *
 * MEMOISED at the bottom of this file, and it must stay that way: the stage's
 * zoom is a CSS transform over this figure, so none of the props below change
 * when the reviewer zooms — but without React.memo every wheel tick would still
 * re-run this component, hand <Plot/> a freshly built `data` array, and make
 * Plotly redraw a 12-lead figure at wheel rate.
 *
 * @param props
 */
function ModernEcgGrid(props: ModernEcgGridProps) {
    const {speed, amplitude, ecgData, layout, windowStartSec, longRecords} = props;
    const theme = useTheme<ModernTheme>();
    const m = theme.modern;

    const geometry = computeEcgGeometry({ecgData, layout, speed, gain: amplitude, windowStartSec, longRecords});
    const {
        cols, rows, perColumn, firstSample, mmPerSample, speedScale, columnWidthMm, rowPitchMm,
        rhythmLead, rhythmBaselineMm, rhythmSamples,
        xMin, xMax, yMin, yMax,
    } = geometry;

    // Signals are addressed by lead name; the positional fallback covers
    // payloads whose `lead` string does not match LeadEnum.
    const byLead: {[lead: string]: Signal} = {};
    ecgData.forEach(signal => {
        if (signal && signal.lead) {
            byLead[String(signal.lead).toUpperCase()] = signal
        }
    });

    function signalFor(lead: LeadEnum): Signal | null {
        return byLead[lead] || ecgData[LEAD_ORDER.indexOf(lead)] || null
    }

    const data: Object[] = [];

    const toMillimetres = (value: number) => (value * (amplitude / 10) / 100);

    const labelRiseMm = Math.min(14, rowPitchMm * 0.32);

    const generateGridLines =(spacing: number, color: string, lineWidth: number) => {
        const lines: Partial<Plotly.Shape>[] = [];

        // vertical
        for (let x = Math.ceil(xMin); x <= xMax; x += spacing) {
            if (x % 5 === 0 && spacing === 1) {
                continue
            }
            lines.push({
                layer: "below",
                type: "line",
                x0: x,
                x1: x,
                y0: yMin,
                y1: yMax,
                line: {color, width: lineWidth},
            });
        }

        // horizontal
        for (let y = yMin; y <= yMax; y += spacing) {
            if (y % 5 === 0 && spacing === 1) {
                continue
            }
            lines.push({
                layer: "below",
                type: "line",
                x0: xMin,
                x1: xMax,
                y0: y,
                y1: y,
                line: {color, width: lineWidth},
            });
        }

        return lines
    };

    const smallGrid: Partial<Plotly.Shape>[] = generateGridLines(1, m.gridMinor, 0.5);
    const largeGrid: Partial<Plotly.Shape>[] = generateGridLines(5, m.gridMajor, 1.5);

    // Column dividers, so a 3 x 4 page reads as four consecutive time windows
    // rather than one continuous 10 s strip. They stop above the rhythm strip:
    const dividerYMin = rhythmBaselineMm === null ? yMin : rhythmBaselineMm + (rowPitchMm / 2);
    const dividers: Partial<Plotly.Shape>[] = [];
    for (let column = 1; column < cols; column++) {
        const x = (column * (columnWidthMm + COLUMN_GAP_MM)) - (COLUMN_GAP_MM / 2);

        dividers.push({
            layer: "below",
            type: "line",
            x0: x,
            x1: x,
            y0: dividerYMin,
            y1: yMax,
            line: {color: m.ink3, width: 1},
        });
    }

    function renderStrip(signal: Signal) {
        for (let rowIndex = 0; rowIndex < geometry.stripRows; rowIndex++) {
            const baseline = TOP_BASELINE_MM - (rowIndex * rowPitchMm);
            const start = firstSample + (rowIndex * perColumn);
            const rowData = signal.data.slice(start, start + perColumn);

            if (rowData.length === 0) {
                return
            }

            data.push({
                type: "scatter",
                mode: "text",
                layer: "above",
                x: [-15 * speedScale],
                y: [baseline],
                text: signal.lead,
                textfont: {
                    color: m.beat,
                    weight: "bold",
                    size: 15,
                },
            });

            data.push({
                type: "scatter",
                mode: "text",
                layer: "above",
                x: [3 * speedScale],
                y: [baseline + labelRiseMm],
                text: formatClock(start / geometry.sampleRate),
                textfont: {
                    color: m.ink3,
                    size: 11,
                },
            });

            data.push({
                x: rowData.map((_: number, index: number) => index * mmPerSample),
                y: rowData.map((value: number) => toMillimetres(value) + baseline),
                name: signal.lead,
                type: "scatter",
                mode: "lines",
                line: {width: 1.5, color: m.trace},
            });

            const pulse = computePulse(geometry, baseline + toMillimetres(rowData[0]), amplitude);

            data.push({
                x: pulse.x,
                y: pulse.y,
                type: "scatter",
                mode: "lines",
                line: {width: 1.5, color: m.trace},
            });
        }
    }

    const stripSignal = geometry.strip
        ? (ecgData.filter(signal => signal && signal.data && signal.data.length > 0)[0] || null)
        : null;

    if (stripSignal !== null) {
        renderStrip(stripSignal)
    }

    rows.forEach((rowLeads, rowIndex) => {
        const baseline = TOP_BASELINE_MM - (rowIndex * rowPitchMm);
        let rowInitialValue = 0;
        let rowDrawn = false;

        rowLeads.forEach((lead, columnIndex) => {
            const signal = signalFor(lead);

            if (!signal || !signal.data || signal.data.length === 0) {
                return
            }

            const start = firstSample + (columnIndex * perColumn);
            const leadData = signal.data.slice(start, start + perColumn);

            if (leadData.length === 0) {
                return
            }

            const xOffset = columnIndex * (columnWidthMm + COLUMN_GAP_MM);

            rowDrawn = true;

            if (columnIndex === 0) {
                rowInitialValue = toMillimetres(leadData[0])
            }

            // Lead label. 12 x 1 keeps Classic's placement in the left gutter;
            // the multi-column pages label each cell in place, because there is
            // no gutter to the left of columns 2..n.
            data.push({
                type: "scatter",
                mode: "text",
                layer: "above",
                x: [cols === 1 ? -15 * speedScale : xOffset + (3 * speedScale)],
                y: [cols === 1 ? baseline : baseline + labelRiseMm],
                // the signal's own label, not the enum member, so leads keep the
                // casing the cart sent them with ("aVR", not "AVR")
                text: signal.lead || lead,
                textfont: {
                    color: m.beat,
                    weight: "bold",
                    size: 15,
                },
            });

            data.push({
                x: leadData.map((_: number, index: number) => xOffset + (index * mmPerSample)),
                y: leadData.map((value: number) => toMillimetres(value) + baseline),
                name: lead,
                type: "scatter",
                mode: "lines",
                line: {width: 1.5, color: m.trace},
            });
        });

        if (!rowDrawn) {
            return
        }
        const pulse = computePulse(geometry, baseline + rowInitialValue, amplitude);

        data.push({
            x: pulse.x,
            y: pulse.y,
            type: "scatter",
            mode: "lines",
            line: {width: 1.5, color: m.trace},
        });
    });
    const rhythmSignal = rhythmLead === null ? null : signalFor(rhythmLead);

    if (rhythmLead !== null && rhythmBaselineMm !== null && rhythmSignal !== null && rhythmSignal.data) {
        const stripData = rhythmSignal.data.slice(firstSample, firstSample + rhythmSamples);

        if (stripData.length > 0) {
            data.push({
                x: stripData.map((_: number, index: number) => index * mmPerSample),
                y: stripData.map((value: number) => toMillimetres(value) + rhythmBaselineMm),
                name: rhythmLead,
                type: "scatter",
                mode: "lines",
                line: {width: 1.5, color: m.trace},
            });

            const rhythmPulse = computePulse(
                geometry,
                rhythmBaselineMm + toMillimetres(stripData[0]),
                amplitude,
            );

            data.push({
                x: rhythmPulse.x,
                y: rhythmPulse.y,
                type: "scatter",
                mode: "lines",
                line: {width: 1.5, color: m.trace},
            });

            // Pushed AFTER the trace, unlike the grid rows' labels, so nothing
            // can paint over it. The strip is the one row whose label sits
            // inside the trace's own swing: `labelRiseMm` is 0.32 of the row
            // pitch, and 6 x 2 clamps that pitch to its 14 mm minimum, which
            // put the "II" only 4.48 mm above the baseline — under the peaks.
            // Drawing it last is what makes it visible there; 3 x 4's 18 mm
            // floor happened to clear the trace, which is why only 6 x 2 lost
            // its label.
            data.push({
                type: "scatter",
                mode: "text",
                x: [3 * speedScale],
                y: [rhythmBaselineMm + labelRiseMm],
                text: rhythmSignal.lead || rhythmLead,
                textfont: {
                    color: m.beat,
                    weight: "bold",
                    size: 15,
                },
            });
        }
    }

    data.push({
        type: "scatter",
        mode: "text",
        layer: "above",
        x: [-5 * speedScale],
        y: [Math.max(yMin + 2, (TOP_BASELINE_MM - (rows.length * rowPitchMm)) + 2)],
        textfont: {
            color: m.ink,
            weight: "bold",
            size: 12,
        },
    });

    const margin: Partial<Plotly.Margin> = {t: 0, r: 0, l: 0, b: 0};

    const plotLayout: Partial<Plotly.Layout> = {
        width: geometry.widthPx,
        height: geometry.heightPx,
        margin: margin,
        dragmode: false,
        showlegend: false,
        hovermode: false,
        // the whole point of the fork: follow the reading-room surface
        paper_bgcolor: m.paper,
        plot_bgcolor: m.paper,
        xaxis: {
            range: [xMin, xMax],
            showgrid: false,
            zeroline: false,
            title: {},
            showticklabels: false,
        },
        yaxis: {
            range: [yMin, yMax],
            showgrid: false,
            zeroline: false,
            title: {},
            showticklabels: false,
        },
        shapes: [...smallGrid, ...largeGrid, ...dividers],
    };

    return (
      <Plot
        data={data}
        layout={plotLayout}
        config={{responsive: true, displayModeBar: false}}
      />
    )
}

export {ModernEcgGrid}

export default React.memo(ModernEcgGrid)
